import type { DMAnalyticsData } from "../../hooks/useDMAnalytics";
import type { SampleType } from "../../hooks/useDMSamples";
import MetricInfoIcon from "../ui/MetricInfoIcon";
import CountUp from "../ui/CountUp";
import type { EngagedPerSetterRow } from "../../hooks/useEngagedPerSetter";
import type { SetterReplyTurnRow } from "../../hooks/useReplyTurnDistributionAll";
import { getBucketColor } from "./proof/bucketColors";

// =============================================================================
// DM HEALTH HERO CARDS — 4 stat cards (New Chats / Engaged / Conversations Started / Conversations Worked)
// =============================================================================
// Extracted from DMHealthTab Section 1 so the same cards can render in
// /admin/dm-analytics (interactive, opens sample modals) AND /admin/portal
// (display-only — pass no onOpenSample). Drift sub-stat under "Conversations
// Worked" is hidden when interactive=false (internal-coaching signal).
// =============================================================================

export interface DMHealthHeroCardsProps {
  data: DMAnalyticsData;
  dayCount: number;
  /**
   * If provided, cards become clickable and open the corresponding sample modal.
   * Omit (or pass undefined) for display-only mode (e.g. client portal).
   */
  onOpenSample?: (type: SampleType, title: string) => void;
  /** Per-setter engagement breakdown for ENGAGED card. Omit for display-only mode. */
  engagedPerSetter?: EngagedPerSetterRow[];
  /** Reply-turn rows for CONVERSATIONS STARTED mini-chart. Omit for display-only mode. */
  replyTurnRows?: SetterReplyTurnRow[];
  /** Setter bar click handler. Receives the literal setterId (UUID-shaped string) or the sentinel "__UNASSIGNED__". */
  onSetterBarClick?: (setterId: string) => void;
  /** Mini-chart bar click handler. Fired with no arg — opens the existing __ALL__ reply-turn modal. */
  onShallowBarClick?: () => void;
}

export default function DMHealthHeroCards({
  data,
  dayCount,
  onOpenSample,
  engagedPerSetter,
  replyTurnRows,
  onSetterBarClick,
  onShallowBarClick,
}: DMHealthHeroCardsProps) {
  const interactive = Boolean(onOpenSample);
  const cardClass = interactive
    ? "liquid-glass rounded-xl p-5 cursor-pointer hover:shadow-lg transition-all"
    : "liquid-glass rounded-xl p-5 anim-lift";

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {/* New Chats */}
      <div className={cardClass} onClick={() => onOpenSample?.("new_dm", "New Chats")}>
        <p className="token-11 text-glass-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
          New Chats
          <MetricInfoIcon metricKey="newDMChats" getSamples={() => []} />
        </p>
        <p className="token-38 text-ds-accent">
          <CountUp value={data.totalNewDMsLeadReplied} />
        </p>
        <p className="token-14 text-glass-secondary mt-2">
          <CountUp value={data.totalNewDMsLeadReplied / dayCount} format={(n) => n.toFixed(1)} />
          /day · lead replied
        </p>
        <p className="token-11 text-glass-muted mt-2 leading-snug">
          <span className="text-glass-secondary">
            <CountUp value={data.totalNewDMs} format={(n) => Math.round(n).toLocaleString()} />{" "}
            total conversations created
          </span>
          {/* This gap has THREE causes, not one, and the old copy named the wrong
              one. It read "{N} leads never replied (couldn't be assigned to a
              setter)" — two different explanations welded onto one number, with
              the parenthetical blaming assignment for all of it.

              The headline count is `total_new_dms_lead_replied`
              (get_dm_analytics_fast.sql:162-171), which drops a conversation for
              ANY of: first_lead_msg_date IS NULL (the lead genuinely never
              replied), ghl_assigned_user_id IS NULL (nobody owns it, so no setter
              can see it in their GHL inbox), or a dismiss_type that has not
              resurfaced (killed or dismissed).

              Splitting the number three ways needs three more counts from the
              RPC. Until someone asks for that, say what is true: these did not
              make the headline, and here is why they might not have. Do not pick
              one cause and print it as fact. */}
          {data.totalNewDMs > data.totalNewDMsLeadReplied && (
            <>
              {" · "}
              <span className="text-ds-red">
                <CountUp
                  value={data.totalNewDMs - data.totalNewDMsLeadReplied}
                  format={(n) => Math.round(n).toLocaleString()}
                />{" "}
                not counted above
              </span>
              <span className="text-glass-muted">
                {" "}
                (lead never replied, no setter assigned, or dismissed)
              </span>
            </>
          )}
        </p>
        <p className="token-11 text-glass-muted italic leading-snug mt-3 pt-3 border-t border-black/[0.05]">
          New conversations where the lead actually engaged. ManyChat blasts the lead never replied
          to are excluded from the headline number — they show in the gray subtext above as
          &ldquo;total created&rdquo; for completeness.
        </p>
      </div>

      {/* Replied — setter-touched metric (data.totalEngaged) KEPT per Mako's Apr-27 ruling; only the
          LABEL changed Engaged→Replied (Jul 22) so the word stops implying a two-way exchange. The
          true two-way metric (2 lead + 2 setter replies) keeps the name "Engaged" on the funnel tile. */}
      <div className={cardClass} onClick={() => onOpenSample?.("engaged", "Replied Conversations")}>
        <p className="token-11 text-glass-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
          Replied
          <MetricInfoIcon metricKey="engaged" getSamples={() => []} />
        </p>
        <p className="token-38 text-ds-green">
          <CountUp value={data.totalEngaged} />
        </p>
        <p className="token-11 text-glass-muted mt-1">setter replied</p>
        <p className="token-11 text-glass-muted italic leading-snug mt-3 pt-3 border-t border-black/[0.05]">
          Of those new conversations, how many had a real human setter actually reply at least once.
          (Not a two-way measure — the lead need not have replied back.)
        </p>
        {engagedPerSetter && engagedPerSetter.length > 0 && onSetterBarClick && (
          <div className="mt-3 pt-3 border-t border-black/[0.05]">
            <p className="token-10 text-glass-muted uppercase tracking-wider mb-2">
              By setter ·{" "}
              {engagedPerSetter.length === 1 ? "1 setter" : `${engagedPerSetter.length} setters`}
            </p>
            <div className="space-y-1.5">
              {engagedPerSetter.map((row) => {
                const total = row.engagedCount + row.unengagedCount;
                if (total === 0) return null;
                const engagedPct = (row.engagedCount / total) * 100;
                const unengagedPct = 100 - engagedPct;
                const isUnassigned = row.setterId === null;
                const targetSetterId = isUnassigned ? "__UNASSIGNED__" : row.setterId!;
                return (
                  <button
                    key={row.setterId ?? "__UNASSIGNED__"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetterBarClick(targetSetterId);
                    }}
                    className="w-full text-left group"
                    aria-label={`${row.setterName}: ${row.unengagedCount} ignored, ${row.engagedCount} engaged`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span
                        className={`token-11 truncate max-w-[60%] ${isUnassigned ? "text-glass-muted italic" : "text-glass"}`}
                      >
                        {row.setterName}
                      </span>
                      <span className="token-10 text-ds-red font-medium tabular-nums">
                        <CountUp value={row.unengagedCount} /> ignored
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-black/[0.04] overflow-hidden flex">
                      <div
                        className={isUnassigned ? "bg-glass-muted/40" : "bg-ds-green"}
                        style={{ width: `${engagedPct}%` }}
                      />
                      <div
                        className="bg-ds-red group-hover:opacity-80 transition-opacity"
                        style={{ width: `${unengagedPct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Conversations Started — first_msg_date attribution */}
      <div
        className={cardClass}
        onClick={() => onOpenSample?.("real_convo", "Conversations Started")}
      >
        <p className="token-11 text-glass-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
          Conversations Started
          <MetricInfoIcon metricKey="conversations" getSamples={() => []} />
        </p>
        <p className="token-38 text-ds-purple">
          <CountUp value={data.totalRealConvos} />
        </p>
        <p className="token-14 text-glass-secondary mt-2">4+ reply turns · first msg date</p>
        <p className="token-11 text-glass-muted italic leading-snug mt-3 pt-3 border-t border-black/[0.05]">
          Of conversations <em>born</em> in this window, how many ever went deep — setter and lead
          bounced replies back and forth at least 4 times. Counted by birth-date.
        </p>
        {replyTurnRows &&
          onShallowBarClick &&
          (() => {
            const bucketsToShow = ["1", "2", "3"];
            const aggregated = bucketsToShow.map((label) => {
              let count = 0;
              let leadCount = 0;
              for (const row of replyTurnRows) {
                const b = row.buckets.find((x) => x.bucket === label);
                if (b) {
                  count += b.count;
                  leadCount += b.leadCount;
                }
              }
              return { label, count, leadCount };
            });
            const totalShallow = aggregated.reduce((acc, b) => acc + b.count, 0);
            if (totalShallow === 0) return null;
            const maxCount = Math.max(...aggregated.map((b) => b.count), 1);
            return (
              <div className="mt-3 pt-3 border-t border-black/[0.05]">
                <p className="token-10 text-glass-muted uppercase tracking-wider mb-2">
                  Shallow conversations ·{" "}
                  <CountUp value={totalShallow} format={(n) => Math.round(n).toLocaleString()} />{" "}
                  total
                </p>
                <div className="space-y-1">
                  {aggregated.map((b) => {
                    const pct = (b.count / maxCount) * 100;
                    const leadPct = b.count > 0 ? (b.leadCount / b.count) * 100 : 0;
                    return (
                      <button
                        key={b.label}
                        onClick={(e) => {
                          e.stopPropagation();
                          onShallowBarClick();
                        }}
                        className="w-full text-left group flex items-center gap-2"
                        aria-label={`${b.label} reply: ${b.count} conversations, ${b.leadCount} lead-replied-last`}
                      >
                        <span className="token-10 text-glass-muted w-12 flex-shrink-0">
                          {b.label} reply
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-black/[0.04] overflow-hidden relative">
                          <div
                            className={`absolute inset-y-0 left-0 ${getBucketColor(b.label)} group-hover:opacity-80 transition-opacity`}
                            style={{ width: `${pct * (leadPct / 100)}%` }}
                          />
                          <div
                            className={`absolute inset-y-0 ${getBucketColor(b.label)} opacity-30`}
                            style={{
                              left: `${pct * (leadPct / 100)}%`,
                              width: `${pct * (1 - leadPct / 100)}%`,
                            }}
                          />
                        </div>
                        <span className="token-10 text-glass-secondary w-10 text-right tabular-nums">
                          <CountUp value={b.count} />
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="token-10 text-glass-muted italic mt-2">
                  Solid = lead replied last (ball in setter&apos;s court). Faded = setter replied
                  last.
                </p>
              </div>
            );
          })()}
      </div>

      {/* Conversations Worked — work_date attribution + Sales Cycle Drift sub-stat */}
      <div
        className={cardClass}
        onClick={() => onOpenSample?.("real_convo", "Conversations Worked")}
      >
        <p className="token-11 text-glass-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
          Conversations Worked
          <MetricInfoIcon metricKey="dmConvosWorked" getSamples={() => []} />
        </p>
        <p className="token-38 text-ds-purple">
          <CountUp value={data.totalDeepConvos} />
        </p>
        <p className="token-14 text-glass-secondary mt-2">
          4+ reply turns · work date
          {interactive &&
            (() => {
              const drift = data.totalDeepConvos - data.totalRealConvos;
              if (drift === 0) return null;
              const driftColor =
                drift <= 0 ? "text-ds-green" : drift <= 5 ? "text-ds-amber" : "text-ds-red";
              return (
                <MetricInfoIcon metricKey="salesCycleDrift" getSamples={() => []}>
                  <span className={`ml-2 token-11 ${driftColor}`}>
                    (drift{" "}
                    <CountUp
                      value={drift}
                      format={(n) => `${Math.round(n) > 0 ? "+" : ""}${Math.round(n)}`}
                    />
                    )
                  </span>
                </MetricInfoIcon>
              );
            })()}
        </p>
        <p className="token-11 text-glass-muted italic leading-snug mt-3 pt-3 border-t border-black/[0.05]">
          Same 4+ reply threshold, but counted by the day the setter <em>did the work</em> —
          includes older conversations.
          {interactive &&
            (() => {
              const drift = data.totalDeepConvos - data.totalRealConvos;
              if (drift === 0) return null;
              return drift < 0 ? (
                <> Negative drift = a lot of new conversations are still cooking.</>
              ) : (
                <> Positive drift = working through backlog faster than fresh ones replenish.</>
              );
            })()}
        </p>
      </div>
    </div>
  );
}
